import React, { useEffect, useMemo } from 'react'
import Modal from '../../../components/Modal/Modal'
import { Button } from '@demandlocal/components'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import EditUserInfo from './EditUserInfo'
import EditUserOrganizations from './EditUserOrganizations'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { useGetRoles } from '../../../api/utils/useGetRoles'
import { Role } from '../../../lib/types/Role'
import { useCreateUser } from '../../../api/admin/useCreateUser'
import toast from 'react-hot-toast'
import { usePostValidateEmail } from '../../../api/admin/usePostValidateEmail'
import EditUserPermissions from './EditUserPermissions'
import { match } from 'ts-pattern'
import { queryClient } from '../../../api/queryClient'

export default function AdminUserAdd(): JSX.Element {
  const formContext = useForm({
    mode: 'onSubmit',
  })

  const selectedRole = formContext.watch('role_id')

  const navigate = useNavigate()
  const location = useLocation()
  const path = location?.pathname?.split('/')?.pop() ?? 'info'
  const { data: roles } = useGetRoles()
  const rolesOptions = useMemo(
    () =>
      roles?.map((role: Role) => ({
        label: role.user_role,
        value: role.id,
      })) ?? [],
    [roles],
  )

  useEffect(() => {
    document.documentElement.style.overflow = 'hidden'

    return () => {
      document.documentElement.style.overflow = 'unset'
    }
  }, [])

  const {
    handleSubmit,
    setError,
    formState: { isSubmitting },
  } = formContext
  const { mutateAsync: createUser } = useCreateUser()
  const { mutateAsync: postValidateEmail } = usePostValidateEmail()

  const onSubmit = async (values: Partial<FieldValues>): Promise<void> => {
    if (values.role_id?.value > 1) {
      if (path === 'info') {
        const validateEmailResponse = await postValidateEmail({
          data: { email: values.email },
        })
        if (validateEmailResponse?.valid) {
          return navigate('organizations')
        } else {
          return setError('email', {
            type: 'manual',
            message: 'Email already exists',
          })
        }
      }
      if (path === 'organizations') return navigate('permissions')
    }

    const toastId = toast.loading('Creating user...')

    const organizations = values?.organizations
      ?.filter((org) => org.has_all_dealerships_assigned)
      ?.map((org) => org.value)
    const dealerships = values?.organizations
      ?.filter((org) => !org.has_all_dealerships_assigned)
      ?.map((org) =>
        org.dealerships
          .filter((dealership) => dealership.checked)
          .map((dealership) => ({
            dealership_id: dealership.id,
            organization_id: org.value,
          })),
      )
      ?.flat()

    await createUser(
      {
        data: {
          ...values,
          organizations,
          dealerships,
          role_id: values?.role_id?.valueOf().value,
        },
      },
      {
        onSuccess: () => {
          toast.success('User created successfully', { id: toastId })
          queryClient.invalidateQueries('users')
          navigate('../')
        },
      },
    )
  }
  return (
    <Modal isOpen={true} key={'AdminUserAdd'}>
      <FormProvider {...formContext}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={'flex flex-1 flex-col h-screen'}>
          <div
            className={
              'flex flex-1 flex-col px-10 max-w-[1920px] mx-auto w-full h-screen'
            }>
            <div className={'text-3xl font-bold mt-12'}>Add new user</div>
            <div className={'font-Spartan overflow-scroll h-full'}>
              <Routes>
                <Route
                  path={'info'}
                  element={<EditUserInfo rolesOptions={rolesOptions} />}
                />
                <Route
                  path={'organizations'}
                  element={<EditUserOrganizations isAddNewUser={true} />}
                />
                <Route path={'permissions'} element={<EditUserPermissions />} />
                <Route path={'*'} element={<Navigate to={'info'} />} />
              </Routes>
            </div>
            <div className={'flex justify-between py-5 border-t-2'}>
              <Button
                disabled={isSubmitting}
                variant={'secondary'}
                type={'button'}
                animate={false}
                onClick={() => {
                  if (path === 'info') navigate('../')
                  else navigate(-1)
                }}>
                {path === 'info' ? 'Cancel' : 'Back'}
              </Button>
              <Button animate={false} disabled={isSubmitting} variant={'primary'}>
                {selectedRole?.value > 1
                  ? match(path)
                      .with('info', () => 'Next: Assign organizations')
                      .with('organizations', () => 'Next: Assign permissions')
                      .otherwise(() => 'Save')
                  : 'Save'}
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </Modal>
  )
}
